<script setup lang="ts">
defineProps<{ scene: 'welcome' | 'jobs' | 'photos' | 'offline' | 'success' }>()
</script>

<template>
  <!-- The same ink, paper and spot colors tie the five field illustrations together. -->
  <svg
    viewBox="0 0 240 168"
    fill="none"
    aria-hidden="true"
    focusable="false"
    class="field-art"
  >
    <g
      stroke="#352e42"
      stroke-width="3.6"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <!-- A pocket camera with a wrist strap and little boots. -->
      <g v-if="scene === 'welcome'">
        <ellipse
          cx="123"
          cy="149"
          rx="66"
          ry="6"
          fill="#352e42"
          opacity=".12"
          stroke="none"
        />
        <path d="M104 119q1 14-6 23m43-23q0 12 6 21" />
        <path
          d="M99 138q-13-3-19 4-3 6 15 5 7 0 8-5zm44-1q9-5 18 3 6 7-12 6-6 0-6-9z"
          fill="#352e42"
        />
        <g transform="rotate(-8 120 87)">
          <path
            d="M178 64c32-23 49-13 40 4-8 15-30 14-31 32"
            stroke-width="5"
          />
          <rect x="52" y="44" width="143" height="82" rx="20" fill="#f39878" />
          <rect x="65" y="36" width="27" height="12" rx="5" fill="#352e42" />
          <path d="M135 48v-6q0-6 6-6h18q5 0 7 6l2 7" fill="#ffe9cc" />
          <rect x="46" y="46" width="141" height="78" rx="18" fill="#ffbc99" />
          <path d="M48 72h137" stroke-width="2" />
          <rect
            x="57"
            y="55"
            width="23"
            height="10"
            rx="4"
            fill="#fff5df"
            stroke-width="2.6"
          />
          <circle cx="170" cy="60" r="3.5" fill="#352e42" stroke="none" />
          <circle cx="123" cy="89" r="31" fill="#f39878" />
          <circle cx="120" cy="87" r="26" fill="#fff5df" />
          <g fill="#352e42" stroke="none">
            <ellipse cx="111" cy="85" rx="2.7" ry="4" />
            <ellipse cx="128" cy="85" rx="2.7" ry="4" />
          </g>
          <path d="M114 94q6 7 12-1" stroke-width="2.8" />
          <path d="M58 106h12m-12 6h8" stroke-width="2.5" />
        </g>
        <path
          d="M33 25q0 11-10 13 10 0 11 12 2-10 11-13-10-1-12-12z"
          fill="#fff5df"
          stroke-width="2.3"
        />
        <path d="m181 18 3-7m4 14 7-3M31 102l-7 3m9 4-4 5" stroke-width="2.5" />
      </g>
      <!-- A folded street map and a pin that doubles as a tiny house. -->
      <g v-else-if="scene === 'jobs'">
        <ellipse
          cx="118"
          cy="146"
          rx="75"
          ry="6"
          fill="#352e42"
          opacity=".1"
          stroke="none"
        />
        <path
          d="m46 83 48-16 43 13 42-14 22 63-54 16-45-12-54 11z"
          fill="#fff3cf"
        />
        <path d="m94 67 8 66 45 12-10-65z" fill="#b6dfc8" />
        <path
          d="m51 115 41-17 41 11 49-19m-58 29 13 23"
          stroke="#82765d"
          stroke-width="2"
          stroke-dasharray="5 5"
        />
        <path
          d="M153 47c0 22-28 49-28 49S95 68 95 49a29 29 0 0 1 58-2z"
          fill="#ffbc99"
        />
        <path d="m111 48 13-12 14 12m-23-2v14h18V46" fill="#fff3cf" />
        <path d="m61 48 4-7m5 13 7-2m93-19 4-6" />
      </g>
      <!-- Two instant prints with a little landscape. -->
      <g v-else-if="scene === 'photos'">
        <ellipse
          cx="120"
          cy="145"
          rx="67"
          ry="5"
          fill="#352e42"
          opacity=".1"
          stroke="none"
        />
        <rect
          x="46"
          y="43"
          width="99"
          height="89"
          rx="9"
          transform="rotate(-11 96 88)"
          fill="#cbbcec"
        />
        <g transform="rotate(8 136 82)">
          <rect x="80" y="26" width="108" height="106" rx="9" fill="#fff6e6" />
          <rect
            x="90"
            y="36"
            width="88"
            height="67"
            rx="4"
            fill="#d8cbed"
            stroke-width="2.5"
          />
          <circle cx="151" cy="54" r="8" fill="#fff6e6" stroke-width="2.4" />
          <path
            d="m91 90 24-27 23 21 13-12 26 22v7H91z"
            fill="#b6dfc8"
            stroke-width="2.5"
          />
          <path d="m117 117 33 0" stroke-width="2.5" />
        </g>
        <path d="m57 25 2-9m-10 13-6-3m152 87 7 2" />
      </g>
      <g v-else-if="scene === 'offline'">
        <path
          d="M67 126c-38 0-43-43-11-52 0-38 50-54 72-27 27-20 60-4 60 25 38 1 43 53 5 54z"
          fill="#d8cbed"
          stroke-width="6"
        />
        <path d="M87 83q30-24 59-2m-46 16q16-12 31-1" stroke-width="7" />
        <circle cx="117" cy="109" r="4.5" fill="#352e42" stroke="none" />
        <path d="m152 68-37 47" stroke="#d8cbed" stroke-width="13" />
        <path d="m154 67-37 47" stroke-width="6" />
      </g>
      <g v-else>
        <path
          d="M67 126c-38 0-43-43-11-52 0-38 50-54 72-27 27-20 60-4 60 25 38 1 43 53 5 54z"
          fill="#b6dfc8"
          stroke-width="6"
        />
        <path d="m96 85 16 16 34-35" stroke-width="7" />
        <path d="m185 27 3-11m8 21 10-5M40 132l-8 6" stroke-width="5" />
      </g>
    </g>
  </svg>
</template>
